import { ref, onUnmounted } from "vue";
import { useAnalytics } from "@/core/composables/useAnalytics";
import { Logger } from "@/core/utilities";
import type { Ref } from "vue";

const DEBUG_PREFIX = "[FormTracking]";

export interface IFormTrackingOptions {
  /** Unique identifier for the form (e.g., "login", "checkout_address") */
  formId: string;
  /** Human-readable form name (e.g., "Login Form", "Checkout Address Form") */
  formName: string;
  /** Whether to automatically track form start on first interaction. Default: true */
  autoTrackStart?: boolean;
}

export interface IFormTrackingReturn {
  /** Whether the form start event has been fired */
  formStarted: Ref<boolean>;
  /** Call this when user first interacts with the form (if autoTrackStart is false) */
  trackFormStart: () => void;
  /** Call this when form is submitted successfully */
  trackFormSubmitSuccess: (params?: Record<string, unknown>) => void;
  /** Call this when form submission fails */
  trackFormSubmitError: (errorMessage?: string, fieldErrors?: string[], params?: Record<string, unknown>) => void;
  /** Event handler for input focus - use with @focus on form inputs */
  onInputFocus: () => void;
  /** Reset tracking state (useful for multi-step forms) */
  resetTracking: () => void;
}

/**
 * Composable for tracking form interactions with Google Analytics.
 *
 * @example
 * ```vue
 * <script setup>
 * const { onInputFocus, trackFormSubmitSuccess, trackFormSubmitError } = useFormTracking({
 *   formId: "login",
 *   formName: "Login Form",
 * });
 *
 * async function handleSubmit() {
 *   try {
 *     await submitForm();
 *     trackFormSubmitSuccess();
 *   } catch (error) {
 *     trackFormSubmitError(error.message);
 *   }
 * }
 * </script>
 *
 * <template>
 *   <form @submit.prevent="handleSubmit">
 *     <input @focus="onInputFocus" />
 *   </form>
 * </template>
 * ```
 */
export function useFormTracking(options: IFormTrackingOptions): IFormTrackingReturn {
  const { formId, formName, autoTrackStart = true } = options;
  const { analytics } = useAnalytics();

  const formStarted = ref(false);

  function trackFormStart(): void {
    if (formStarted.value) {
      return;
    }

    formStarted.value = true;
    try {
      analytics("formStart", formId, formName);
    } catch (e) {
      Logger.error(DEBUG_PREFIX, "trackFormStart", e);
    }
  }

  function onInputFocus(): void {
    if (autoTrackStart) {
      trackFormStart();
    }
  }

  function trackFormSubmitSuccess(params?: Record<string, unknown>): void {
    try {
      analytics("formSubmit", formId, formName, true, params);
    } catch (e) {
      Logger.error(DEBUG_PREFIX, "trackFormSubmitSuccess", e);
    }
  }

  function trackFormSubmitError(errorMessage?: string, fieldErrors?: string[], params?: Record<string, unknown>): void {
    try {
      analytics("formSubmit", formId, formName, false, {
        ...params,
        error_message: errorMessage,
        field_errors: fieldErrors,
      });
    } catch (e) {
      Logger.error(DEBUG_PREFIX, "trackFormSubmitError", e);
    }
  }

  function resetTracking(): void {
    formStarted.value = false;
  }

  onUnmounted(() => {
    // Clean up if needed
  });

  return {
    formStarted,
    trackFormStart,
    trackFormSubmitSuccess,
    trackFormSubmitError,
    onInputFocus,
    resetTracking,
  };
}
